/*
 * Copyright (C) 2011 - present Instructure, Inc.
 *
 * This file is part of Canvas.
 *
 * Canvas is free software: you can redistribute it and/or modify it under
 * the terms of the GNU Affero General Public License as published by the Free
 * Software Foundation, version 3 of the License.
 *
 * Canvas is distributed in the hope that it will be useful, but WITHOUT ANY
 * WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 * A PARTICULAR PURPOSE. See the GNU Affero General Public License for more
 * details.
 *
 * You should have received a copy of the GNU Affero General Public License along
 * with this program. If not, see <http://www.gnu.org/licenses/>.
 */

import $ from 'jquery'
import React, {Suspense} from 'react'
import {render} from '@canvas/react'
import NavigationView from './backbone/views/NavigationView'
import {ErrorBoundary} from '@instructure/platform-error-boundary'
import {Spinner} from '@instructure/ui-spinner'
import {Text} from '@instructure/ui-text'
import CourseColorSelector from './react/components/CourseColorSelector'
import CourseImageSelector from './react/components/CourseImageSelector'
import LicenseHelpIcon from './react/components/LicenseHelpIcon'
import VisibilityHelpIcon from './react/components/VisibilityHelpIcon'
import configureStore from './react/store/configureStore'
import initialState from './react/store/initialState'
import './jquery/index'
import '@canvas/grading-standards'
import {useScope as createI18nScope} from '@canvas/i18n'
import ready from '@instructure/ready'

import type {GlobalEnv} from '@canvas/global/env/GlobalEnv'
import type {EnvCourseSettings} from '@canvas/global/env/EnvCourse'
import SettingsTabs from '../../shared/tabs/SettingsTabs'

declare const ENV: GlobalEnv & EnvCourseSettings

const I18n = createI18nScope('course_settings')

const BlueprintLockOptions = React.lazy(() => import('./react/components/BlueprintLockOptions'))
const CourseTemplateDetails = React.lazy(() => import('./react/components/CourseTemplateDetails'))
const CourseAvailabilityOptions = React.lazy(
  () => import('./react/components/CourseAvailabilityOptions'),
)
const QuantitativeDataOptions = React.lazy(
  () => import('./react/components/QuantitativeDataOptions'),
)
const CourseDefaultDueTime = React.lazy(() => import('./react/components/CourseDefaultDueTime'))
const Integrations = React.lazy(() => import('@canvas/integrations/react/courses/Integrations'))
const CourseApps = React.lazy(() => import('./react/components/CourseApps'))
const CourseNavigationSettings = React.lazy(
  () => import('./react/components/CourseNavigationSettings'),
)
const CanvasCourseCriteria = React.lazy(() => import('./react/components/CanvasCourseCriteria'))

const Loading = () => <Spinner size="x-small" renderTitle={I18n.t('Loading')} />
const ErrorMessage = () => (
  <div className="bcs_check-box">
    <Text color="danger">{I18n.t('Unable to load this control')}</Text>
  </div>
)

ready(() => {
  const canEditContent = ENV.FEATURES?.course_navigation_and_feature_options_permissions
    ? ENV.PERMISSIONS?.manage_course_details === true
    : ENV.PERMISSIONS?.manage_course_content_edit !== false

  const blueprint = document.getElementById('blueprint_menu')
  if (blueprint) {
    render(
      <Suspense fallback={<Loading />}>
        <ErrorBoundary errorComponent={<ErrorMessage />}>
          <BlueprintLockOptions
            isMasterCourse={ENV.IS_MASTER_COURSE}
            disabledMessage={ENV.DISABLED_BLUEPRINT_MESSAGE}
            generalRestrictions={ENV.BLUEPRINT_RESTRICTIONS}
            useRestrictionsbyType={ENV.USE_BLUEPRINT_RESTRICTIONS_BY_OBJECT_TYPE}
            restrictionsByType={ENV.BLUEPRINT_RESTRICTIONS_BY_OBJECT_TYPE}
          />
        </ErrorBoundary>
      </Suspense>,
      blueprint,
    )
  }

  const courseTemplate = document.getElementById('course_template_details')
  if (courseTemplate) {
    const isEditable = courseTemplate.getAttribute('data-is-editable') === 'true'

    render(
      <Suspense fallback={<Loading />}>
        <ErrorBoundary errorComponent={<ErrorMessage />}>
          <CourseTemplateDetails isEditable={isEditable} />
        </ErrorBoundary>
      </Suspense>,
      courseTemplate,
    )
  }

  // @ts-expect-error
  const navView = new NavigationView({el: $('#tab-navigation-mount')})

  // @ts-expect-error
  $(() => navView.render())

  if (canEditContent) {
    render(
      <CourseImageSelector
        store={configureStore(initialState)}
        courseId={ENV.COURSE_ID}
        setting="image"
      />,
      $('.CourseImageSelector__Container')[0],
    )

    const bannerImageContainer = document.getElementById('course_banner_image_selector_container')
    if (bannerImageContainer) {
      render(
        <CourseImageSelector
          store={configureStore(initialState)}
          courseId={ENV.COURSE_ID}
          setting="banner_image"
          wide={true}
        />,
        bannerImageContainer,
      )
    }
  }

  const availabilityOptionsContainer = document.getElementById('availability_options_container')
  if (availabilityOptionsContainer) {
    render(
      <Suspense fallback={<Loading />}>
        <ErrorBoundary errorComponent={<ErrorMessage />}>
          <CourseAvailabilityOptions
            canManage={ENV.PERMISSIONS.edit_course_availability && canEditContent}
            viewPastLocked={ENV.RESTRICT_STUDENT_PAST_VIEW_LOCKED}
            viewFutureLocked={ENV.RESTRICT_STUDENT_FUTURE_VIEW_LOCKED}
          />
        </ErrorBoundary>
      </Suspense>,
      availabilityOptionsContainer,
    )
  }

  const restrictQuantitativeDataContainer = document.getElementById(
    'restrict_quantitative_data_options_container',
  )
  if (restrictQuantitativeDataContainer) {
    render(
      <Suspense fallback={<Loading />}>
        <QuantitativeDataOptions
          canManage={ENV.CAN_EDIT_RESTRICT_QUANTITATIVE_DATA && canEditContent}
        />
      </Suspense>,
      restrictQuantitativeDataContainer,
    )
  }

  const defaultDueTimeContainer = document.getElementById('default_due_time_container')
  if (defaultDueTimeContainer) {
    const defaultValue = defaultDueTimeContainer.dataset.defaultDueTime
    if (!defaultValue) throw new Error('attr data-default-due-time is missing on container')
    render(
      <Suspense fallback={<Loading />}>
        <CourseDefaultDueTime
          canManage={ENV.PERMISSIONS.manage && canEditContent}
          container={defaultDueTimeContainer}
          value={defaultValue}
        />
      </Suspense>,
      defaultDueTimeContainer,
    )
  }

  const licenseHelpContainer = document.getElementById('license_mount')
  if (licenseHelpContainer) {
    render(<LicenseHelpIcon />, licenseHelpContainer)
  }

  const visibilityHelpContainer = document.getElementById('visibility_mount')
  if (visibilityHelpContainer) {
    render(<VisibilityHelpIcon />, visibilityHelpContainer)
  }

  if (ENV.COURSE_COLORS_ENABLED) {
    const courseColorPickerContainer = document.getElementById('course_color_picker_container')
    if (courseColorPickerContainer) {
      render(
        <CourseColorSelector courseColor={ENV.COURSE_COLOR || undefined} />,
        courseColorPickerContainer,
      )
    }
  }

  const integrationsContainer = document.getElementById('tab-integrations-mount')
  if (integrationsContainer) {
    render(
      <Suspense fallback={<Loading />}>
        <ErrorBoundary errorComponent={<ErrorMessage />}>
          <Integrations />
        </ErrorBoundary>
      </Suspense>,
      integrationsContainer,
    )
  }

  const appsMountpoint = document.getElementById('tab-apps-mount')
  if (appsMountpoint) {
    render(
      <Suspense fallback={<Loading />}>
        <ErrorBoundary errorComponent={<ErrorMessage />}>
          <CourseApps />
        </ErrorBoundary>
      </Suspense>,
      appsMountpoint,
    )
  }

  const tabsMountpoint = document.getElementById('course_settings_tabs_mount')
  if (tabsMountpoint && tabsMountpoint.dataset.props) {
    const {tabs} = JSON.parse(tabsMountpoint.dataset.props)
    render(
      <Suspense fallback={<Loading />}>
        <ErrorBoundary errorComponent={<ErrorMessage />}>
          <SettingsTabs tabs={tabs} />
        </ErrorBoundary>
      </Suspense>,
      tabsMountpoint,
    )
  }

  const navSettingsContainer = document.getElementById('course_navigation_settings_container')
  if (navSettingsContainer) {
    render(
      <Suspense fallback={<Loading />}>
        <ErrorBoundary errorComponent={<ErrorMessage />}>
          <CourseNavigationSettings
            onSubmit={tabs => {
              const form = document.querySelector<HTMLFormElement>(
                'form#course_navigation_settings_form',
              )
              if (!form) {
                console.error('Course navigation settings form not found')
                return
              }
              const tabsJsonHiddenInput =
                form.querySelector<HTMLInputElement>('input[name="tabs_json"]')
              if (!tabsJsonHiddenInput) {
                console.error('Tabs JSON input not found')
                return
              }
              tabsJsonHiddenInput.value = JSON.stringify(tabs)
              form.submit()
            }}
          />
        </ErrorBoundary>
      </Suspense>,
      navSettingsContainer,
    )
  }

  const criteriaContainer = document.getElementById('tab-criteria-mount')
  if (criteriaContainer) {
    render(
      <Suspense fallback={<Loading />}>
        <ErrorBoundary errorComponent={<ErrorMessage />}>
          <CanvasCourseCriteria />
        </ErrorBoundary>
      </Suspense>,
      criteriaContainer,
    )
  }

  const sidebarCriteriaContainer = document.getElementById('criteria-mount')
  if (sidebarCriteriaContainer) {
    render(
      <Suspense fallback={<Loading />}>
        <ErrorBoundary errorComponent={<ErrorMessage />}>
          <CanvasCourseCriteria />
        </ErrorBoundary>
      </Suspense>,
      sidebarCriteriaContainer,
    )
  }
})
